import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { View } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { BulletedList } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bulletedList/BulletedList'
import { BulletIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/BulletIcon'
import { XIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/XIcon'
import { PressableOpacity } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/pressableOpacity/PressableOpacity'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { Modal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/modal/Modal'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

interface CareInsightsOnboardingModalProps {
  isModalOpen: boolean
  onClose: () => void
}

const Container = styled.View(({ theme }) => ({
  gap: theme.spacing['16px'],
}))

const HeaderContainer = styled.View({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
})

const BulletIconContainer = styled.View(({ theme }) => ({
  paddingTop: theme.spacing['12px'],
}))

export const CareInsightsOnboardingModal: React.FC<CareInsightsOnboardingModalProps> = ({ isModalOpen, onClose }) => {
  const { formatMessage } = useIntl()
  const { colors, spacing } = useTheme()

  const bulletPointTexts = useMemo(
    () => [
      {
        value: formatMessage(
          {
            defaultMessage: '<bold>Put clients first:</bold> Help you provide the best possible care.',
            description: 'Label for bullet point about putting clients first',
          },
          {
            bold: (text: string) => (
              <BodyText size={BodyTextSize.DEFAULT} bold text={text} color={colors.textSecondary} />
            ),
          },
        ),
        bulletIcon: (
          <BulletIconContainer>
            <BulletIcon size={3} fillColor={colors.textSecondary} />
          </BulletIconContainer>
        ),
      },
      {
        value: formatMessage(
          {
            defaultMessage: '<bold>Celebrate success:</bold> Show where you are doing a great job.',
            description: 'Label for bullet point about celebrating success',
          },
          {
            bold: (text: string) => (
              <BodyText size={BodyTextSize.DEFAULT} bold text={text} color={colors.textSecondary} />
            ),
          },
        ),
        bulletIcon: (
          <BulletIconContainer>
            <BulletIcon size={3} fillColor={colors.textSecondary} />
          </BulletIconContainer>
        ),
      },
      {
        value: formatMessage(
          {
            defaultMessage: '<bold>Offer suggestions:</bold> Find opportunities to continue growing your skills.',
            description: 'Label for bullet point about offering suggestions',
          },
          {
            bold: (text: string) => (
              <BodyText size={BodyTextSize.DEFAULT} bold text={text} color={colors.textSecondary} />
            ),
          },
        ),
        bulletIcon: (
          <BulletIconContainer>
            <BulletIcon size={3} fillColor={colors.textSecondary} />
          </BulletIconContainer>
        ),
      },
    ],
    [colors.textSecondary, formatMessage],
  )

  const modalContents = (
    <Container testID={tID('CareInsightsOnboardingModal-container')}>
      <HeaderContainer>
        <Title
          size={TitleSize.MEDIUM}
          text={formatMessage({
            defaultMessage: 'Understanding your care insights',
            description: 'Title for the care insights onboarding modal',
          })}
          testID={tID('CareInsightsOnboardingModal-title')}
        />
        <PressableOpacity
          testID={tID('CareInsightsOnboardingModal-close')}
          onPress={onClose}
          accessibilityLabel={formatMessage({
            defaultMessage: 'Close',
            description: 'Accessibility label for close button',
          })}
        >
          <XIcon size={24} fillColor={colors.iconDefault} />
        </PressableOpacity>
      </HeaderContainer>
      <View>
        <BodyText
          size={BodyTextSize.DEFAULT}
          text={formatMessage({
            defaultMessage:
              'At Lyra, we believe that sharing feedback helps everyone grow. This report is designed to:',
            description: 'Introduction text for the care insights onboarding modal',
          })}
          testID={tID('CareInsightsOnboardingModal-intro')}
          color={colors.textSecondary}
        />
        <BulletedList
          data={bulletPointTexts}
          size={BodyTextSize.DEFAULT}
          bulletColor={colors.textSecondary}
          spaceBetween={0}
          style={{ marginLeft: spacing['12px'] }}
          textColor={colors.textSecondary}
        />
      </View>
      <BodyText
        size={BodyTextSize.DEFAULT}
        text={formatMessage({
          defaultMessage:
            "While data is helpful, it doesn't show everything. Please think of these insights as a starting point for reflection.",
          description: 'Closing text for the care insights onboarding modal',
        })}
        testID={tID('CareInsightsOnboardingModal-closing')}
        color={colors.textSecondary}
      />
    </Container>
  )

  return (
    <Modal
      onCloseEnd={onClose}
      onRequestClose={onClose}
      disableBottomSheet
      visible={isModalOpen}
      modalContents={modalContents}
      width='550px'
      closeOnScrim
      showCloseIcon={false}
      accessibilityLabel={formatMessage({
        defaultMessage: 'Understanding your care insights modal',
        description: 'Accessibility label for the care insights onboarding modal',
      })}
    />
  )
}
